import { useCallback, useEffect, useRef, useState } from "react";

import { getVisibleViewportSize } from "./floatingPanelViewport";

const VIEWPORT_MARGIN = 16;

function toNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function resolveMaxSize(maxSize = {}) {
    const { width: viewportW, height: viewportH } = getVisibleViewportSize();
    return {
        width: Math.min(toNumber(maxSize.width, 900), viewportW - VIEWPORT_MARGIN * 2),
        height: Math.min(toNumber(maxSize.height, viewportH * 0.8), viewportH - VIEWPORT_MARGIN * 2)
    };
}

function clampSize(size, minSize, maxSize) {
    const max = resolveMaxSize(maxSize);
    const minW = toNumber(minSize?.width, 280);
    const minH = toNumber(minSize?.height, 200);
    return {
        width: Math.max(minW, Math.min(toNumber(size?.width, minW), max.width)),
        height: Math.max(minH, Math.min(toNumber(size?.height, minH), max.height))
    };
}

function applyPanelSize(node, size) {
    if (!node || !size) return;
    node.style.width = `${size.width}px`;
    node.style.height = `${size.height}px`;
}

function resolveInitialPosition(initialPosition) {
    if (initialPosition) {
        return {
            top: Math.round(Number(initialPosition.top) || VIEWPORT_MARGIN + 56),
            left: Math.round(Number(initialPosition.left) || VIEWPORT_MARGIN)
        };
    }
    return { top: VIEWPORT_MARGIN + 56, left: VIEWPORT_MARGIN };
}

export function useFloatingPanelLayout({
    defaultSize = { width: 480, height: 420 },
    minSize = { width: 360, height: 280 },
    maxSize = { width: 900, height: 720 },
    initialPosition = null,
    centerInViewport = false
}) {
    const panelRef = useRef(null);
    const positionInitializedRef = useRef(false);

    const [position, setPosition] = useState(() => resolveInitialPosition(initialPosition));

    const syncPanelSize = useCallback(() => {
        const node = panelRef.current;
        if (!node) return;
        applyPanelSize(node, clampSize(defaultSize, minSize, maxSize));
    }, [defaultSize, minSize, maxSize]);

    useEffect(() => {
        syncPanelSize();
    }, [defaultSize.height, defaultSize.width, syncPanelSize]);

    useEffect(() => {
        if (centerInViewport || !initialPosition || positionInitializedRef.current) return;
        setPosition(resolveInitialPosition(initialPosition));
        positionInitializedRef.current = true;
    }, [centerInViewport, initialPosition]);

    return {
        panelRef,
        isFlexCentered: centerInViewport,
        shellStyle: centerInViewport ? undefined : { top: position.top, left: position.left }
    };
}
